import { useEffect } from 'react';

import { useTracker } from '@archipro-website/tracker';
import { getEcomEventItemList } from '~/modules/tracking/util/gtmHelper';
import { useVariantSelectionState } from '../context/VariantSelectionContext';

interface TrackableProduct {
    ID: number;
    Title: string;
    CanPurchase: boolean;
}

/**
 * Logs the `ViewProduct` analytics event + the GTM ecommerce `view_item`
 * remarketing event for the current product/variant. Shared by the legacy and
 * revamp PDPs so neither loses product-view tracking. Must be called inside a
 * `VariantSelectionProvider` (reads the selected variant for price/variant id).
 */
export const useTrackProductView = (product: TrackableProduct) => {
    const { selectedVariant } = useVariantSelectionState();
    const tracker = useTracker();

    const productID = product.ID;
    const productTitle = product.Title;
    const variantID = selectedVariant?.ID;
    const priceData = selectedVariant?.Price;
    const price = priceData?.Amount ? (priceData.Amount / 100.0).toFixed(2) : 0;
    const currency = priceData?.Currency ?? 'NZD';
    const canPurchase = product.CanPurchase;

    useEffect(() => {
        tracker.log('ViewProduct', {
            url: new URL(window.location.href),
            data: {
                content_ids: productID,
                variant_id: variantID,
                content_type: 'product',
                value: price,
                currency,
            },
        });
    }, [productID, variantID, currency, tracker, price]);

    useEffect(() => {
        if (canPurchase) {
            // ecom remarketing event
            tracker.log('view_item', {
                targetTracker: 'googleTagManagerTracker',
                url: new URL(window.location.href),
                gtmData: getEcomEventItemList(
                    [
                        {
                            item_id: productID,
                            item_name: productTitle,
                            item_variant: variantID,
                            index: 0,
                            price: price,
                            quantity: 1,
                        },
                    ],
                    currency,
                ),
            });
        }
    }, [
        productID,
        productTitle,
        variantID,
        currency,
        tracker,
        price,
        canPurchase,
    ]);
};
